import { Button, Modal, Table } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useMemo } from "react";
import { Purchase } from "types";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import {
    getFulfilledQuantity,
    getMerchandisePurchaseItems,
    getMerchandiseWareLineItems,
} from "@/models/MerchandisePurchaseItem";

interface IncompleteAutomaticFulfillmentModalProps {
    isOpen: boolean;
    purchase: Purchase | null;
    onClose: () => void;
    onModifyPurchase: () => void;
}

export const IncompleteAutomaticFulfillmentModal = ({
    isOpen,
    purchase,
    onClose,
    onModifyPurchase,
}: IncompleteAutomaticFulfillmentModalProps) => {
    const rows = useMemo(() => {
        if (!purchase) {
            return [];
        }

        return getMerchandisePurchaseItems(purchase).flatMap((item) => {
            return getMerchandiseWareLineItems(item).map((lineItem) => {
                const ordered = lineItem.quantity ?? 0;
                const fulfilled = getFulfilledQuantity(item, lineItem.template?.id);

                return {
                    id: lineItem.id,
                    itemName: item.name,
                    variantName: lineItem.name,
                    ordered,
                    fulfilled,
                    toFulfill: Math.max(ordered - fulfilled, 0),
                };
            });
        });
    }, [purchase]);

    return (
        <Modal isOpen={isOpen} size="large" onClose={onClose}>
            <Modal.Header onClose={onClose}>Incomplete Automatic Fulfillment</Modal.Header>
            <Modal.Body>
                <div className="mb-6 rounded-lg bg-danger-lighter p-4 text-danger-darker">
                    There was not enough inventory to fulfill some of the items in this purchase.
                </div>

                <div className="overflow-hidden rounded-lg border border-gray-lighter">
                    <Table className="w-full table-fixed border-none">
                        <Table.Head>
                            <Table.Row>
                                <Table.Header className="w-1/2 text-left">Item</Table.Header>
                                <Table.Header className="w-1/6 text-center">Ordered</Table.Header>
                                <Table.Header className="w-1/6 text-center">Fulfilled</Table.Header>
                                <Table.Header className="w-1/6 text-center">To Fulfill</Table.Header>
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {rows.map((row) => {
                                const isIncomplete = row.toFulfill > 0;

                                return (
                                    <Table.Row key={row.id}>
                                        <Table.Cell>
                                            <div className={clsx("font-bold", isIncomplete && "text-danger")}>
                                                {row.itemName}
                                            </div>
                                            <div className={clsx("text-sm", isIncomplete && "text-danger")}>
                                                {row.variantName}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell className={clsx("text-center", isIncomplete && "text-danger")}>
                                            {row.ordered}
                                        </Table.Cell>
                                        <Table.Cell className={clsx("text-center", isIncomplete && "text-danger")}>
                                            {row.fulfilled}
                                        </Table.Cell>
                                        <Table.Cell className={clsx("text-center", isIncomplete && "text-danger")}>
                                            {row.toFulfill}
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex w-full justify-end space-x-2">
                    <SecondaryButton onClick={onModifyPurchase}>Modify Purchase</SecondaryButton>
                    <Button onClick={onClose}>Close</Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
